import { useState, useEffect, useRef, useCallback } from 'react';

export interface ProctoringViolation {
  type: 'tab_switch' | 'copy_paste' | 'fullscreen_exit' | 'right_click' | 'key_combination' | 'window_blur';
  timestamp: Date;
  details: string;
}

export interface ProctoringStatus {
  isActive: boolean;
  violations: ProctoringViolation[];
  violationCount: number;
  isFullscreen: boolean;
  windowFocused: boolean;
  cameraStream: MediaStream | null;
  microphoneStream: MediaStream | null;
}

interface ProctoringConfig {
  enableTabSwitchDetection?: boolean;
  enableCopyPasteDetection?: boolean;
  enableFullscreenEnforcement?: boolean;
  enableRightClickDisable?: boolean;
  enableKeyboardShortcuts?: boolean;
  enableCameraMonitoring?: boolean;
  enableMicrophoneMonitoring?: boolean;
  maxViolations?: number;
  onViolation?: (violation: ProctoringViolation) => void;
  onMaxViolationsReached?: () => void;
}

export const useProctoring = (config: ProctoringConfig = {}) => {
  const {
    enableTabSwitchDetection = true,
    enableCopyPasteDetection = true,
    enableFullscreenEnforcement = true,
    enableRightClickDisable = true,
    enableKeyboardShortcuts = true,
    enableCameraMonitoring = false,
    enableMicrophoneMonitoring = false,
    maxViolations = 5,
    onViolation,
    onMaxViolationsReached
  } = config;

  const [proctoringStatus, setProctoringStatus] = useState<ProctoringStatus>({
    isActive: false,
    violations: [],
    violationCount: 0,
    isFullscreen: false,
    windowFocused: true,
    cameraStream: null,
    microphoneStream: null
  });

  const violationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastViolationRef = useRef<number>(0);

  const addViolation = useCallback((violation: ProctoringViolation) => {
    const now = Date.now();
    // Prevent spam violations (max 1 per second)
    if (now - lastViolationRef.current < 1000) return;

    lastViolationRef.current = now;

    setProctoringStatus(prev => {
      const newViolations = [...prev.violations, violation];
      const newCount = prev.violationCount + 1;

      // Call violation callback
      onViolation?.(violation);

      // Check max violations
      if (newCount >= maxViolations) {
        onMaxViolationsReached?.();
      }

      return {
        ...prev,
        violations: newViolations,
        violationCount: newCount
      };
    });
  }, [onViolation, onMaxViolationsReached, maxViolations]);

  // Tab switch / window focus detection
  useEffect(() => {
    if (!proctoringStatus.isActive || !enableTabSwitchDetection) return;

    const handleWindowBlur = () => {
      setProctoringStatus(prev => ({ ...prev, windowFocused: false }));
      addViolation({
        type: 'window_blur',
        timestamp: new Date(),
        details: 'Window lost focus - possible tab switch or application change'
      });
    };

    const handleWindowFocus = () => {
      setProctoringStatus(prev => ({ ...prev, windowFocused: true }));
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        addViolation({
          type: 'tab_switch',
          timestamp: new Date(),
          details: 'Page became hidden - tab switch detected'
        });
      }
    };

    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [proctoringStatus.isActive, enableTabSwitchDetection, addViolation]);

  // Copy/Paste detection
  useEffect(() => {
    if (!proctoringStatus.isActive || !enableCopyPasteDetection) return;

    const handleCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      addViolation({
        type: 'copy_paste',
        timestamp: new Date(),
        details: 'Copy operation detected and blocked'
      });
    };

    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      addViolation({
        type: 'copy_paste',
        timestamp: new Date(),
        details: 'Paste operation detected and blocked'
      });
    };

    const handleCut = (e: ClipboardEvent) => {
      e.preventDefault();
      addViolation({
        type: 'copy_paste',
        timestamp: new Date(),
        details: 'Cut operation detected and blocked'
      });
    };

    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('cut', handleCut);

    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('cut', handleCut);
    };
  }, [proctoringStatus.isActive, enableCopyPasteDetection, addViolation]);

  // Right-click disable
  useEffect(() => {
    if (!proctoringStatus.isActive || !enableRightClickDisable) return;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      addViolation({
        type: 'right_click',
        timestamp: new Date(),
        details: 'Right-click context menu attempted'
      });
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [proctoringStatus.isActive, enableRightClickDisable, addViolation]);

  // Keyboard shortcuts detection
  useEffect(() => {
    if (!proctoringStatus.isActive || !enableKeyboardShortcuts) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const forbiddenCombinations = [
        { ctrl: true, key: 'c' }, // Copy
        { ctrl: true, key: 'v' }, // Paste
        { ctrl: true, key: 'x' }, // Cut
        { ctrl: true, key: 'a' }, // Select All
        { ctrl: true, key: 's' }, // Save
        { ctrl: true, key: 'p' }, // Print
        { ctrl: true, key: 'f' }, // Find
        { ctrl: true, key: 'h' }, // History
        { ctrl: true, key: 'j' }, // Downloads
        { ctrl: true, key: 'u' }, // View Source
        { ctrl: true, shift: true, key: 'i' }, // Dev Tools
        { ctrl: true, shift: true, key: 'j' }, // Console
        { ctrl: true, shift: true, key: 'c' }, // Inspector
        { key: 'F12' }, // Dev Tools
        { alt: true, key: 'Tab' }, // Alt+Tab
        { key: 'F5' }, // Refresh
        { ctrl: true, key: 'r' }, // Refresh
        { ctrl: true, key: 't' }, // New Tab
        { ctrl: true, key: 'n' }, // New Window
        { ctrl: true, key: 'w' }, // Close Tab
        { ctrl: true, shift: true, key: 'n' }, // Incognito
      ];

      const isForbidden = forbiddenCombinations.some(combo => {
        return Object.keys(combo).every(key => {
          if (key === 'ctrl') return e.ctrlKey === combo.ctrl;
          if (key === 'alt') return e.altKey === combo.alt;
          if (key === 'shift') return e.shiftKey === combo.shift;
          if (key === 'key') return e.key === combo.key || e.code === combo.key;
          return false;
        });
      });

      if (isForbidden) {
        e.preventDefault();
        e.stopPropagation();
        addViolation({
          type: 'key_combination',
          timestamp: new Date(),
          details: `Forbidden key combination: ${e.ctrlKey ? 'Ctrl+' : ''}${e.altKey ? 'Alt+' : ''}${e.shiftKey ? 'Shift+' : ''}${e.key}`
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [proctoringStatus.isActive, enableKeyboardShortcuts, addViolation]);

  // Fullscreen enforcement
  useEffect(() => {
    if (!proctoringStatus.isActive || !enableFullscreenEnforcement) return;

    const handleFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;
      setProctoringStatus(prev => ({ ...prev, isFullscreen }));

      if (!isFullscreen) {
        addViolation({
          type: 'fullscreen_exit',
          timestamp: new Date(),
          details: 'Exited fullscreen mode'
        });

        // Try to re-enter fullscreen after a short delay
        setTimeout(() => {
          if (proctoringStatus.isActive) {
            document.documentElement.requestFullscreen?.();
          }
        }, 1000);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [proctoringStatus.isActive, enableFullscreenEnforcement, addViolation]);

  // Camera and Microphone monitoring
  const initializeMediaStreams = useCallback(async () => {
    try {
      const constraints: MediaStreamConstraints = {
        video: enableCameraMonitoring,
        audio: enableMicrophoneMonitoring
      };

      if (enableCameraMonitoring || enableMicrophoneMonitoring) {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        setProctoringStatus(prev => ({
          ...prev,
          cameraStream: enableCameraMonitoring ? stream : null,
          microphoneStream: enableMicrophoneMonitoring ? stream : null
        }));
      }
    } catch (error) {
    }
  }, [enableCameraMonitoring, enableMicrophoneMonitoring]);

  const startProctoring = useCallback(async () => {
    // Initialize media streams first (if needed)
    if (enableCameraMonitoring || enableMicrophoneMonitoring) {
      await initializeMediaStreams();
    }

    // Request fullscreen if enabled - this should be called from a user gesture
    // Don't throw error if fullscreen fails - just log it
    if (enableFullscreenEnforcement) {
      try {
        // Check if we can request fullscreen
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        } else {
        }
      } catch (error) {
        // Fullscreen may fail due to browser restrictions (not a user gesture, etc.)
        // This is not a critical error - proctoring can still work without fullscreen
      }
    }

    // Always activate proctoring regardless of fullscreen state
    setProctoringStatus(prev => ({
      ...prev,
      isActive: true,
      violations: [],
      violationCount: 0,
      isFullscreen: !!document.fullscreenElement
    }));
  }, [enableFullscreenEnforcement, enableCameraMonitoring, enableMicrophoneMonitoring, initializeMediaStreams]);

  const stopProctoring = useCallback(() => {
    // Exit fullscreen
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }

    setProctoringStatus(prev => {
      // Stop media streams
      if (prev.cameraStream) {
        prev.cameraStream.getTracks().forEach(track => track.stop());
      }
      if (prev.microphoneStream) {
        prev.microphoneStream.getTracks().forEach(track => track.stop());
      }

      return {
        ...prev,
        isActive: false,
        cameraStream: null,
        microphoneStream: null
      };
    });
  }, []);

  const clearViolations = useCallback(() => {
    setProctoringStatus(prev => ({
      ...prev,
      violations: [],
      violationCount: 0
    }));
  }, []);

  return {
    proctoringStatus,
    startProctoring,
    stopProctoring,
    clearViolations,
    addViolation
  };
};